import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useDelayedFlag, useTran } from "@/hooks";
import {
  useConsolidatedStore,
  useEntryDataStore,
} from "../../hooks/useDataEntryStore";
import { useOrgPathById } from "../../hooks/useOrgPathById";
import { humanizeFormula } from "@/features/esg/utils/formula-display";
import { formatMetricValue } from "../../domains/dataEntry";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { findOrgPathWithStatus } from "@/components/common/organization.utils";

const OrgCell = observer(({ orgName, orgId, path }) => {
  const { orgTree } = useEntryDataStore();
  const pathWithStatus = findOrgPathWithStatus(orgTree, orgId);

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 truncate text-foreground">
        <span className="truncate">{orgName || "—"}</span>
        {orgId && (
          <OrgStatusTag
            status={pathWithStatus[pathWithStatus.length - 1]?.status}
            inactiveDate={
              pathWithStatus[pathWithStatus.length - 1]?.inactiveDate
            }
            className="text-[10px] shrink-0"
          />
        )}
      </div>
      {path && (
        <p
          className="truncate text-xs text-muted-foreground"
          title={`${path} › ${orgName || ""}`}
        >
          {pathWithStatus.length > 0 ? (
            <span className="inline-flex flex-wrap items-center gap-0.5">
              {pathWithStatus.map((node, idx) => {
                const isLast = idx === pathWithStatus.length - 1;
                return (
                  <span
                    key={`${node.id}-${idx}`}
                    className="inline-flex items-center gap-0.5"
                  >
                    {idx > 0 && (
                      <span className="px-0.5 text-muted-foreground/50">›</span>
                    )}
                    <span
                      className={
                        isLast
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                      }
                    >
                      {node.name}
                    </span>
                    <OrgStatusTag
                      status={node.status}
                      inactiveDate={node.inactiveDate}
                      className="text-[10px]"
                    />
                  </span>
                );
              })}
            </span>
          ) : (
            <>
              {path}
              <span className="px-1">›</span>
              <span className="text-primary font-medium">{orgName}</span>
            </>
          )}
        </p>
      )}
    </div>
  );
});

const SUMMARY_LABEL = {
  CUMULATIVE: "Total",
  LATEST: "Latest",
  AVERAGE: "Average",
};

const STICKY_SUMMARY = "sticky right-0 bg-inherit border-l border-border";

const formatValue = formatMetricValue;

const ConsolidatedBreakdownPanel = observer(({ breakdownKey }) => {
  const { t } = useTran();
  const { breakdownById } = useConsolidatedStore();
  const orgPathById = useOrgPathById();
  const data = breakdownById.get(breakdownKey);
  const showSkeleton = useDelayedFlag(!data || Boolean(data?.loading));

  if (showSkeleton) return <LoadingState />;
  if (data?.error) return <ErrorState t={t} />;
  if (data?.ratioBreakdown)
    return <RatioBreakdown data={data} orgPathById={orgPathById} t={t} />;
  if (data?.note) return <NoteState note={data.note} />;
  if (!data?.items?.length) return <EmptyState t={t} />;

  return <Matrix data={data} orgPathById={orgPathById} t={t} />;
});

const LoadingState = () => (
  <div className="px-12 py-3 space-y-2">
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-4 w-1/3" />
  </div>
);

const ErrorState = ({ t }) => (
  <div className="px-12 py-3 text-sm text-destructive">
    {t("Failed to load per-org breakdown.")}
  </div>
);

const NoteState = ({ note }) => (
  <div className="px-12 py-3 text-sm text-muted-foreground italic">{note}</div>
);

const EmptyState = ({ t }) => (
  <div className="px-12 py-3 text-sm text-muted-foreground">
    {t("No descendant orgs reported for this metric.")}
  </div>
);

const GROUP_LEVEL_NOTE = "This KPI will be presented at group level.";

const isGroupLevelNaStatus = (s) =>
  s === "qualitative_na" || s === "parent_only_na";

const isInlineNaStatus = (s) => isGroupLevelNaStatus(s) || s === "remark_na";

const GroupLevelFooter = ({ t }) => (
  <p className="text-xs text-muted-foreground italic mt-2">
    {t(GROUP_LEVEL_NOTE)}
  </p>
);

const Matrix = ({ data, orgPathById, t }) => {
  const isQualitative = data.kind === "qualitative";
  const groupLevelOnly = isQualitative || data.kind === "parent_only";
  const [naExpanded, setNaExpanded] = useState(false);

  const naItems = data.items.filter((i) => isGroupLevelNaStatus(i.status));
  const normalItems = data.items.filter((i) => !isGroupLevelNaStatus(i.status));

  const rawYears = groupLevelOnly
    ? data.years || []
    : (data.years || []).filter((y) =>
        normalItems.some((item) => item.perYear?.[y]),
      );
  const isSingleYear = rawYears.length === 1;
  const years = isSingleYear ? [] : rawYears;
  const summaryLabel = isSingleYear
    ? t("Result")
    : isQualitative
      ? t("Latest")
      : t(SUMMARY_LABEL[data.temporalAggregation] || "Total");
  const colCount = years.length + 2;

  return (
    <div className="px-12 py-3">
      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
        {t("Per-org × per-year")}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <MatrixHeader years={years} summaryLabel={summaryLabel} t={t} />
          <tbody>
            {normalItems.map((item) =>
              item.status === "mismatch" ? (
                <MismatchRow
                  key={item.orgId || `parent-${item.orgName}`}
                  item={item}
                  yearCount={years.length}
                  orgPathById={orgPathById}
                  t={t}
                />
              ) : (
                <DataRow
                  key={item.orgId || `parent-${item.orgName}`}
                  item={item}
                  years={years}
                  orgPathById={orgPathById}
                  t={t}
                />
              ),
            )}
            {naItems.length > 0 && (
              <NaToggleRow
                count={naItems.length}
                expanded={naExpanded}
                onToggle={() => setNaExpanded((v) => !v)}
                colCount={colCount}
                t={t}
              />
            )}
            {naExpanded &&
              naItems.map((item) => (
                <DataRow
                  key={item.orgId || `na-${item.orgName}`}
                  item={item}
                  years={years}
                  orgPathById={orgPathById}
                  t={t}
                />
              ))}
          </tbody>
        </table>
      </div>
      {groupLevelOnly && <GroupLevelFooter t={t} />}
    </div>
  );
};

const NaToggleRow = ({ count, expanded, onToggle, colCount, t }) => (
  <tr>
    <td colSpan={colCount} className="py-2 pr-4">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {count} {t("sub-organisations (n.a)")}
      </button>
    </td>
  </tr>
);

const MatrixHeader = ({ years, summaryLabel, t }) => (
  <thead>
    <tr className="text-xs text-muted-foreground border-b border-border/40">
      <th className="py-1.5 pr-4 text-left font-medium">{t("Organization")}</th>
      {years.map((y) => (
        <th key={y} className="py-1.5 px-3 text-right font-medium tabular-nums">
          {y}
        </th>
      ))}
      <th className={`py-1.5 pl-4 text-right font-medium ${STICKY_SUMMARY}`}>
        {summaryLabel}
      </th>
    </tr>
  </thead>
);

const MismatchRow = ({ item, yearCount, orgPathById, t }) => (
  <tr className="border-b border-border/40 last:border-0">
    <td className="py-2 pr-4 text-foreground">
      <OrgCell
        orgName={item.orgName}
        orgId={item.orgId}
        path={orgPathById.get(String(item.orgId || "")) || ""}
      />
    </td>
    <td colSpan={yearCount} className="py-2 text-center">
      <span
        className="rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-medium"
        title={t(
          "Unit incompatible with metric default — fix the entry's unit or add a conversion factor.",
        )}
      >
        {t("Unit mismatch")}
      </span>
      {item.total?.unit && (
        <span className="ml-1.5 text-muted-foreground text-xs">
          {item.total.unit}
        </span>
      )}
    </td>
    <td className={`py-2 pl-4 text-right tabular-nums ${STICKY_SUMMARY}`}>—</td>
  </tr>
);

const DataRow = ({ item, years, orgPathById, t }) => {
  const isNa = isInlineNaStatus(item.status);
  const naTitle = isGroupLevelNaStatus(item.status)
    ? t(GROUP_LEVEL_NOTE)
    : undefined;
  return (
    <tr className="border-b border-border/40 last:border-0">
      <td className="py-2 pr-4 text-foreground">
        <OrgCell
          orgName={item.orgName}
          orgId={item.orgId}
          path={orgPathById.get(String(item.orgId || "")) || ""}
        />
      </td>
      {years.map((y) =>
        isNa ? (
          <NaCell key={y} title={naTitle} />
        ) : (
          <YearCell key={y} cell={item.perYear?.[y]} t={t} />
        ),
      )}
      <td
        className={`py-2 pl-4 text-right tabular-nums font-medium ${STICKY_SUMMARY}`}
      >
        {isNa ? (
          <NaInline title={naTitle} />
        ) : (
          <>
            {item.total?.unit === "Year"
              ? String(item.total?.value ?? "")
              : formatValue(item.total?.value)}
            {item.total?.unit && (
              <span className="ml-1 text-muted-foreground text-xs">
                {item.total.unit}
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

const NaCell = ({ title }) => (
  <td
    className="py-2 px-3 text-right text-muted-foreground italic"
    title={title}
  >
    n.a
  </td>
);

const NaInline = ({ title }) => (
  <span className="text-muted-foreground italic font-normal" title={title}>
    n.a
  </span>
);

// Truncate long string values (free-text qualitative entries) but keep
// the full value available on hover.
const YearCell = ({ cell, t }) => (
  <td className="py-2 px-3 text-right tabular-nums">
    {cell ? (
      <span
        className="inline-block max-w-[18rem] truncate align-bottom"
        title={typeof cell.value === "string" ? cell.value : undefined}
      >
        {/* {formatValue(cell.value)} */}
      </span>
    ) : (
      <span
        className="text-muted-foreground"
        title={t("No entry for this year.")}
      >
        —
      </span>
    )}
  </td>
);

// Special-case for WEIGHTED_RATIO: per-(org, year) cell shows the
// underlying refs from the formula plus the resulting ratio. The Σ row
// at the bottom is the only mathematically valid summary — it sums each
// ref independently and re-evaluates the formula on the totals (which
// IS the headline). Plain "sum the Ratio column" would be wrong, so we
// show "→" instead of a sum on that column.
const RatioBreakdown = ({ data, orgPathById, t }) => {
  const {
    formula,
    hasFormula,
    refCodes,
    refTitles,
    rows,
    totals,
    headline,
    headlineUnit,
  } = data.ratioBreakdown;
  const [naExpanded, setNaExpanded] = useState(false);

  const showRatioColumn = hasFormula !== false;
  const ratioColLabel = formula ? t("Ratio") : t("Total");
  // PARENT_ONLY sub-entry mode: parent row carries the aggregate and
  // contributing children render as n.a. Year column has no meaning here
  // (the aggregate spans the full window), and the Σ Combined footer
  // duplicates the parent row, so both get suppressed.
  // const isParentOnlyShape = rows.some((r) => r.status === "parent_only_na");
  const isParentOnlyShape =
    rows.length > 0 && rows.every((r) => r.year == null);
  const distinctYears = new Set(
    rows.map((r) => r.year).filter((y) => y != null),
  );
  const showYearColumn = !isParentOnlyShape && distinctYears.size > 1;
  const showCombinedFooter = !isParentOnlyShape;
  const colCount =
    refCodes.length + (showRatioColumn ? 2 : 1) + (showYearColumn ? 1 : 0);

  const naRows = rows.filter((r) => r.status === "parent_only_na");
  const normalRows = rows.filter((r) => r.status !== "parent_only_na");

  const renderRow = (r) => {
    // To display n.a for Sub-entry ration on consolidated and overview
    const isParentOnlyNa = r.status === "parent_only_na";
    const isDisplayNa = Boolean(r.displayNa);
    const isNa = isParentOnlyNa || isDisplayNa;

    return (
      <tr
        key={`${r.orgId || "co"}-${r.year ?? "agg"}`}
        className="border-b border-border/40 last:border-0"
      >
        <td className="py-2 pr-4 text-foreground">
          <OrgCell
            orgName={r.orgName}
            path={orgPathById.get(String(r.orgId || "")) || ""}
          />
        </td>
        {showYearColumn && (
          <td className="py-2 px-3 text-right tabular-nums">{r.year ?? "—"}</td>
        )}
        {refCodes.map((c) => (
          // To display n.a for Sub-entry ration on consolidated and overview
          <td
            key={c}
            className={`py-2 px-3 text-right tabular-nums ${
              isParentOnlyNa || (isDisplayNa && r.refs?.[c] == null)
                ? "italic text-muted-foreground"
                : ""
            }`}
          >
            {isNa ? "n.a" : r.refs?.[c] != null ? formatValue(r.refs[c]) : "—"}
          </td>
        ))}
        {showRatioColumn && (
          <td
            className={`py-2 pl-4 text-right tabular-nums ${isNa ? "italic text-muted-foreground" : "text-muted-foreground"}`}
          >
            {isNa
              ? "n.a"
              : r.ratio != null
                ? `${formatValue(r.ratio)}${formula ? "%" : ""}`
                : "—"}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="px-12 py-3">
      <div className="mb-2 flex items-baseline gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {formula ? t("Ratio components") : t("Per-position breakdown")}
        </p>
        {formula && (
          <code className="text-[11px] text-muted-foreground">
            {humanizeFormula(formula)}
          </code>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/40 text-xs text-muted-foreground">
              <th className="py-1.5 pr-4 text-left font-medium">
                {t("Organization")}
              </th>
              {showYearColumn && (
                <th className="py-1.5 px-3 text-right font-medium">
                  {t("Year")}
                </th>
              )}
              {refCodes.map((c, i) => (
                <th
                  key={c}
                  className="py-1.5 px-3 text-right font-medium tabular-nums"
                  title={refTitles?.[i]}
                >
                  {c}
                </th>
              ))}
              {showRatioColumn && (
                <th className="py-1.5 pl-4 text-right font-medium">
                  {ratioColLabel}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  className="py-3 text-center text-sm text-muted-foreground"
                >
                  {t("No data to break down.")}
                </td>
              </tr>
            )}
            {normalRows.map(renderRow)}
            {naRows.length > 0 && (
              <NaToggleRow
                count={naRows.length}
                expanded={naExpanded}
                onToggle={() => setNaExpanded((v) => !v)}
                colCount={colCount}
                t={t}
              />
            )}
            {naExpanded && naRows.map(renderRow)}
            {rows.length > 0 && showCombinedFooter && (
              <tr className="border-t-2 border-border bg-muted/30">
                <td className="py-2 pr-4 font-semibold">{t("Σ Combined")}</td>
                {showYearColumn && <td className="py-2 px-3" />}
                {refCodes.map((c) => (
                  <td
                    key={c}
                    className="py-2 px-3 text-right font-semibold tabular-nums"
                  >
                    {totals[c] != null ? formatValue(totals[c]) : "—"}
                  </td>
                ))}
                {showRatioColumn && (
                  <td className="py-2 pl-4 text-right font-semibold tabular-nums">
                    {headline != null ? formatValue(headline) : "—"}
                    {headlineUnit && (
                      <span className="ml-1 text-muted-foreground text-xs">
                        {headlineUnit}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {isParentOnlyShape && <GroupLevelFooter t={t} />}
      {data.note && (
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          {data.note}
        </p>
      )}
    </div>
  );
};

export default ConsolidatedBreakdownPanel;
